import * as React from 'react';
import {
  Box,
  focusOutlineOuter,
  GDSSpace,
  GDSStyleObject,
} from '@leagueplatform/genesis-core';

interface PaginationContainerProps {
  scrollOffset?: GDSSpace | string;
  selectedPage: number;
  labelId: string;
  css?: GDSStyleObject;
}

export const PaginationContainer = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<PaginationContainerProps>
>(
  (
    { children, css, scrollOffset = '$three', selectedPage, labelId = '' },
    _ref,
  ) => {
    const [currentPage, setCurrentPage] = React.useState(selectedPage);
    const [isFocusing, setIsFocusing] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    const containerRef = _ref ?? ref;

    React.useEffect(() => {
      if (selectedPage !== currentPage) {
        setCurrentPage(selectedPage);
        setIsFocusing(true);
      }

      if (isFocusing) {
        ref?.current?.focus();
        ref.current?.scrollIntoView({ behavior: 'smooth' });
        setIsFocusing(false);
      }
    }, [currentPage, isFocusing, selectedPage]);

    return (
      <Box
        aria-labelledby={labelId}
        css={{
          scrollMarginBlockStart: scrollOffset,
          '&&:focus': {
            outline: 'none',
          },
          '&&:focus-visible': {
            ...focusOutlineOuter,
          },
          ...css,
        }}
        tabIndex={-1}
        ref={containerRef}
        aria-live="polite"
        as="section"
      >
        {children}
      </Box>
    );
  },
);
